import MessageWithLogo from "components/MessageWithLogo";
import useOrganisation from "contexts/OrganisationContext/useOrganisation";
import moment from "moment";
import { useCallback, useRef } from "react";
import ActivityFeedService from "services/ActivityFeedService";
import useAllowedAthleteMeasurementFilter from "hooks/useAllowedAthleteMeasurementFilter";
import useMeasurementsFilter from "../hooks/useMeasurementsFilter";
import useMeasurementsSubscription from "../hooks/useMeasurementsSubscription";
import ShowMoreActivityProvider from "../providers/ShowMoreActivityProvider";
import useLiveActivityFeedStore from "./LiveActivityFeed.hooks";
import LiveActivityFeedVirtualTable from "./LiveActivityFeedVirtualTable";
import { useTranslation } from "react-i18next";

interface LiveActivityFeedProps {
  startDate: string;
}

function LiveActivityFeed(props: LiveActivityFeedProps) {
  const organisation = useOrganisation();
  const { startDate } = props;
  const oldestCompletedDateRef = useRef<string>();
  const [activity, setUpdatedActivity] = useLiveActivityFeedStore(oldestCompletedDateRef);
  const { t } = useTranslation();

  useMeasurementsSubscription({ organisationId: organisation.id, startDate, limit: 100 }, setUpdatedActivity);

  /**
   * We should probably refactor this to use a more efficient way to get data,
   * something like: https://firebase.google.com/docs/firestore/query-data/query-cursors#paginate_a_query
   * with startAfter(lastVisible) and limit(X) to get the next X items.
   * This way we can safely paginate and if we want to compose we can merge the latest results with the previous ones.
   * This way we avoid the need of ref, two hooks to get the data, a provider to show more and a provider to update the video.
   */
  const showMoreMeasurements = useCallback(async () => {
    const opts = {
      organisationId: organisation.id,
      endDate: moment(oldestCompletedDateRef.current).subtract(1, "millisecond").toISOString(),
      limit: 50,
    };

    try {
      const measurements = await ActivityFeedService.get(opts);
      setUpdatedActivity(measurements);
    } catch (e) {}
  }, [organisation.id, setUpdatedActivity]);

  const allowedAthleteMeasurements = useAllowedAthleteMeasurementFilter(activity);
  const measurements = useMeasurementsFilter(allowedAthleteMeasurements);

  if (!activity.length) {
    return <MessageWithLogo>{t("info.live_no_activities")}</MessageWithLogo>;
  }

  if (activity.length && !measurements.length) {
    return <MessageWithLogo>{t("info.live_no_activities_filter")}</MessageWithLogo>;
  }

  return (
    <ShowMoreActivityProvider showMore={showMoreMeasurements}>
      <LiveActivityFeedVirtualTable measurements={measurements} />
    </ShowMoreActivityProvider>
  );
}

export default LiveActivityFeed;
